import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Hero from "../components/hero/Hero";
import ClosureBar from "../components/site/ClosureBar";
import PopupModal from "../components/site/PopupModal";
import { FactList, GoodToKnow, HoursList, PlaceholderBadge, SectionLabel } from "../components/site/Blocks";
import { PhoneIcon } from "../components/site/Icons";
import useMediaQuery, { DESKTOP, REDUCED_MOTION } from "../components/site/useMediaQuery";
import { getSeason } from "../config/hours";
import { CONTACT, LOCATION } from "../config/location";
import { DISH_STRIP } from "../config/photos";
import { EVENT_FACTS, VOUCHER_FACTS } from "../config/content";

const menuLinks = () => [
    { name: "Speisekarte" },
    { name: "Mittagskarte", note: `${getSeason().lunchDays} 11:30–14:00` },
    { name: "Weinkarte" },
    { name: "Bestellkarte", note: "zum Liefern und Mitnehmen", to: "/bestellen" },
];

// Bildstreifen: auf dem Desktop scroll-gekoppelt seitwärts, mobil per Wischen.
const DishStrip = () => {
    const sectionRef = useRef(null);
    const stripRef = useRef(null);
    const desktop = useMediaQuery(DESKTOP);
    const reducedMotion = useMediaQuery(REDUCED_MOTION);

    useEffect(() => {
        const strip = stripRef.current;
        if (!strip) return undefined;
        if (!desktop || reducedMotion) {
            strip.style.transform = "none";
            return undefined;
        }
        const onScroll = () => {
            const r = sectionRef.current.getBoundingClientRect();
            const vh = window.innerHeight;
            const t = Math.min(1, Math.max(0, (vh * 0.6 - r.top) / (vh * 0.6 + r.height - vh * 0.4)));
            const over = Math.max(0, strip.scrollWidth - window.innerWidth);
            strip.style.transform = `translateX(${-t * over}px)`;
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
        };
    }, [desktop, reducedMotion]);

    return (
        <section ref={sectionRef} aria-label="Gerichte" className="desk:overflow-hidden pt-24 pb-[88px] desk:pb-[140px]">
            <div
                ref={stripRef}
                className="flex gap-5 xwide:gap-6 px-6 desk:px-[var(--gutter)] overflow-x-auto desk:overflow-visible snap-x snap-mandatory desk:snap-none will-change-transform [scrollbar-width:none]"
            >
                {DISH_STRIP.map((dish) => (
                    <figure
                        key={dish.src}
                        className={`m-0 flex-none snap-start w-[72vw] ${
                            dish.wide
                                ? "desk:w-[420px] xwide:w-[600px]"
                                : "desk:w-[340px] desk:pt-20 xwide:w-[480px] xwide:pt-24"
                        }`}
                    >
                        <div className="mask">
                            <img
                                src={dish.src}
                                alt={dish.title}
                                loading="lazy"
                                className="block aspect-[4/5] w-full object-cover"
                            />
                        </div>
                        <figcaption className="mt-3.5 flex justify-between gap-3 text-sm">
                            <span className="font-caslon text-lg">{dish.title}</span>
                            <span className="text-forest/70">{dish.note}</span>
                        </figcaption>
                    </figure>
                ))}
            </div>
        </section>
    );
};

const Home = () => {
    return (
        <>
            <PopupModal />
            {/* Desktop: Hero und Schließhinweis füllen zusammen genau eine Bildschirmhöhe */}
            <div className="desk:flex desk:h-screen desk:min-h-[720px] desk:flex-col">
                <Hero />
                <ClosureBar />
            </div>

            <section id="kueche" className="sec grid-2-3 img-wide pt-[88px] desk:pt-[140px]">
                <SectionLabel>01 — Was wir kochen</SectionLabel>
                <div className="max-w-[820px]">
                    <h2 className="h2-sec reveal mb-8">Griechische und deutsche Küche, saisonal und mediterran.</h2>
                    <div className="body-text grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-10">
                        {/* TODO Inhalt Kunde: Texte prüfen */}
                        <p className="reveal d1 m-0">
                            Vom Grill kommen Lammkoteletts, Rinderfilet und Oktopus. Dazu Meze zum Teilen: Tzatziki,
                            Saganaki, Dolmadakia, Calamari. Die Lammhaxe wird geschmort und mit Kartoffelpüree
                            serviert.
                        </p>
                        <p className="reveal d2 m-0">
                            {getSeason().lunchDaysLong} gibt es mittags unsere Mittagskarte. Dazu griechische Weine und
                            Ouzo, für Kinder gibt es eine eigene Kinderkarte.
                        </p>
                    </div>
                </div>
                {/* Nur auf breiten Schirmen: füllt die rechte Hälfte */}
                <div className="reveal reveal-scale hidden wide:block">
                    <img
                        src="/media/service.jpeg"
                        alt="Im Service"
                        loading="lazy"
                        className="block aspect-[4/5] w-full max-h-[680px] object-cover"
                    />
                </div>
            </section>

            <DishStrip />

            <section id="karten" className="sec sec-y-md grid-3 img-wide bg-leinen">
                <SectionLabel>02 — Karten</SectionLabel>
                <div>
                    <h2 className="h2-sec reveal mb-6">Unsere Speisekarten.</h2>
                    <p className="body-text reveal d1 mb-10 max-w-[420px]">
                        Speisekarte, Mittagskarte und Weinkarte zum Ansehen. Zum Liefern und Mitnehmen:{" "}
                        <Link to="/bestellen" className="underline">
                            online bestellen
                        </Link>
                        .
                    </p>
                    <div className="reveal d2 max-w-[440px] border-t border-forest/20">
                        {menuLinks().map((menu) => (
                            <Link
                                key={menu.name}
                                to={menu.to || `/karte?${menu.name}`}
                                className="flex items-baseline justify-between border-b border-forest/20 py-[18px] no-underline"
                            >
                                <span className="text-[17px]">
                                    {menu.name}
                                    {menu.note && <span className="ml-2 text-sm text-messing-dunkel">{menu.note}</span>}
                                </span>
                                <span aria-hidden="true" className="text-[13px] text-forest/70">
                                    →
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>
                <div className="reveal reveal-scale relative pb-12 pl-12">
                    <img src="/media/meze-tafel.jpeg" alt="Meze-Teller" loading="lazy" className="block aspect-[3/2] w-full object-cover" />
                    <img
                        src="/media/ouzo.jpeg"
                        alt="Ouzo-Flasche auf dem Tisch"
                        loading="lazy"
                        className="absolute bottom-0 left-0 block aspect-[3/4] w-[38%] object-cover shadow-[0_24px_48px_rgba(27,47,36,.2)]"
                    />
                </div>
            </section>

            <section id="ort" className="sec sec-y grid-3 img-wide">
                <SectionLabel>03 — Neue Adresse</SectionLabel>
                <div>
                    <h2 className="h2-sec reveal mb-6">In der <span className="whitespace-nowrap">{LOCATION.venue}.</span></h2>
                    <p className="body-text reveal d1 mb-9 max-w-[420px]">
                        {LOCATION.street}, {LOCATION.city}.{" "}
                        <Link to="/kontakt" className="underline">
                            Anfahrt und Kontakt
                        </Link>
                    </p>
                    <div className="reveal d2">
                        <HoursList />
                    </div>
                </div>
                <div className="reveal reveal-scale relative">
                    <PlaceholderBadge />
                    <img
                        src="/media/aussen.jpg"
                        alt="Außenansicht des Restaurants am Abend"
                        loading="lazy"
                        className="block aspect-[4/5] desk:aspect-[4/3] w-full object-cover object-[55%_50%]"
                    />
                </div>
            </section>

            <section id="feiern" className="sec sec-y grid-2-3 img-wide bg-forest text-creme">
                <SectionLabel light>04 — Veranstaltungen</SectionLabel>
                <div className="max-w-[820px]">
                    <h2 className="h2-sec reveal mb-8">Feiern, Familienfeste, Firmenessen.</h2>
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(320px,1fr))] gap-10 text-[17px] leading-[1.7] text-creme/80">
                        <p className="reveal d1 m-0">
                            Restaurant, Terrasse und Veranstaltungen – fragen Sie uns für Ihre Feier an. Feste bis 200
                            Personen, ab 20 Personen auch samstagmittags.
                        </p>
                        <p className="reveal d2 m-0">
                            <Link to="/feiern#anfrage" className="text-messing-hell no-underline">
                                Feier oder Catering anfragen →
                            </Link>
                            <br />
                            Oder direkt:{" "}
                            <a href={`mailto:${CONTACT.email}`} className="text-messing-hell no-underline">
                                {CONTACT.email}
                            </a>{" "}
                            ·{" "}
                            <a href={CONTACT.phoneHref} className="text-messing-hell no-underline">
                                {CONTACT.phone}
                            </a>
                        </p>
                    </div>
                    {/* Nur auf breiten Schirmen, damit die linke Seite neben den Bildern nicht leer bleibt */}
                    <FactList light rows={EVENT_FACTS} className="reveal d3 mt-12 hidden wide:block max-w-[560px]" />
                </div>
                {/* Ab 1400px rechts neben dem Text, das erste Bild groß */}
                <div className="reveal d3 mt-9 desk:mt-0 grid grid-cols-3 wide:grid-cols-2 gap-4">
                    {["meze-tisch.jpeg", "grillplatte.jpeg", "buffet.jpeg"].map((file, index) => (
                        <img
                            key={file}
                            src={`/media/${file}`}
                            alt=""
                            loading="lazy"
                            className={`block aspect-square w-full object-cover ${
                                index === 0 ? "wide:col-span-2 wide:aspect-[16/9]" : "wide:aspect-[4/3]"
                            }`}
                        />
                    ))}
                </div>
            </section>

            <section id="gutscheine" className="sec sec-y-md grid-2-3 img-wide">
                <SectionLabel>05 — Gutscheine</SectionLabel>
                <div className="max-w-[640px]">
                    <h2 className="h2-sec reveal mb-6">Griechische Gastfreundschaft verschenken.</h2>
                    <p className="body-text reveal d1 mt-0 mb-10">
                        Für Geburtstage, Feiertage oder einfach als Dankeschön: Gutscheine erhalten Sie bei uns im
                        Restaurant.
                    </p>
                    <FactList rows={VOUCHER_FACTS} className="reveal d2 max-w-[560px]" />
                </div>
                {/* Nur auf breiten Schirmen. TODO Inhalt Kunde: Foto des echten Gutscheins */}
                <div className="reveal reveal-scale hidden wide:block">
                    <img
                        src="/media/meze-tafel.jpeg"
                        alt="Meze-Platten auf dem Tisch"
                        loading="lazy"
                        className="block aspect-[3/2] w-full object-cover"
                    />
                </div>
            </section>

            <section id="reservieren" className="sec sec-y-md grid-2-3 border-t border-forest/10 bg-leinen">
                <SectionLabel>06 — Reservieren</SectionLabel>
                <div className="max-w-[820px]">
                    <h2 className="h2-sec reveal mb-7">Tisch reservieren oder kurz anrufen.</h2>
                    <div className="reveal d1 flex flex-wrap items-center gap-x-8 gap-y-4">
                        <Link to="/reservierung" className="btn btn-primary px-8 py-[18px]">
                            Tisch reservieren
                        </Link>
                        <a
                            href={CONTACT.phoneHref}
                            className="inline-flex items-center gap-2.5 border-b border-forest pb-1 font-caslon text-2xl no-underline"
                        >
                            <PhoneIcon />
                            {CONTACT.phone}
                        </a>
                    </div>
                    <p className="reveal d2 mt-6 mb-0 max-w-[480px] text-[15px] leading-[1.6] text-forest/70">
                        Wir bestätigen telefonisch oder per E-Mail. Ab 10 Personen bitte über{" "}
                        <Link to="/feiern" className="underline">
                            Feiern
                        </Link>{" "}
                        anfragen, ab 20 Personen auch samstagmittags.
                    </p>
                </div>
                <GoodToKnow className="reveal d2 mt-10 wide:mt-0 max-w-[560px]" />
            </section>
        </>
    );
};

export default Home;
